import { DEFAULT_COUNTRY_CODE, DEFAULT_LANGUAGE_CODE } from "@nf/constants";
import { ScreenType } from "../../../enums";
import {
  IConfigurationModel,
  IScreenModel,
  IMediaOptionsModel,
  IMediaModel,
  IMediaSearchFilterModel,
  IMediaSearchStateModel,
  IMediaListModel,
  IMediaListOptionsModel,
  IMediaCategoryListModel,
  IUserFavoritesModel,
  IAuthResponseModel,
  IAuthRequestModel,
  IUserDeviceModel,
  IUserModel,
  IUserConsentModel,
  ITokenModel,
  IUserSubscriptionModel,
  IChangePasswordModel,
  IAuthViaTokenRequestModel,
  IPaymentOfferModel,
  ICreatePaymentOrderRequestModel,
  IUserAvailabilityKeysModel,
  IUserSubprofileModel,
  IUserParentalRatingModel,
  IErrorModel,
  IUserInfoModel,
  IChangePasswordResponseModel,
  IMultiSearchStateModel,
  IPeopleSearchFilterModel,
  IPeopleSearchStateModel,
  IAuthSignUpRequestModel,
  IAuthSignUpResponseModel,
  ICancelSubscriptionModel,
  IPauseSubscriptionModel,
  IPlayHistoryListModel,
  IUserPreferencesModel,
  IValidCouponModel,
  IOrderStatusModel,
  IPaymentOrderModel,
  ICreatePaymentDetailsModel,
  IUserUtmSource,
  IProductModel,
} from "../../../models";
import { IDataProvider } from "../IDataProvider";
import { RedBeeMediaDataProvider } from "../RedBeeMedia";
import { SkymillDataProvider } from "../Skymill";
import { TranslationService } from "./services";
import { Observable } from "rxjs";
import {
  ILoginCodeResponseModel,
  ILoginCodeModel,
  IVerifyLoginCodeResponseModel,
  IGetLoginTokenFromCodeModel,
  IOfferSwitchModel,
  ICancelSubscriptionRequestModel,
  IPauseSubscriptionRequestModel,
  ISubscriptionSwitchRequestModel,
} from "../Skymill/models";
import {
  IAssetQueryParameters,
  ISessionModel,
  IUserPlayHistoryRequestModel,
} from "../RedBeeMedia/models";
import { StorageHelper } from "../../../helpers";

export class NordiskFilmDataProvider implements IDataProvider {
  private _RBMProvider = new RedBeeMediaDataProvider();
  private _SkymillProvider = new SkymillDataProvider();
  private tanslationService = new TranslationService();

  init({ language, country }: { language?: string; country?: string }): void {
    const _language = language || DEFAULT_LANGUAGE_CODE;
    const _country = country || DEFAULT_COUNTRY_CODE;
    this.setLanguage(_language);
    this.setCountry(_country);
    this.initHttpFactory(_country);
  }

  initHttpFactory(country: string): void {
    this._SkymillProvider.initHttpFactory(country);
    this._RBMProvider.initHttpFactory(country);
  }

  setLanguage(language: string): void {
    this._RBMProvider.setLanguage(language);
  }

  setCountry(country: string): void {
    this._RBMProvider.setCountry(country);
  }

  getTranslation(language: string) {
    return this.tanslationService.getTranslation(language);
  }

  async getConfiguration(): Promise<IConfigurationModel> {
    try {
      const locales = await this._SkymillProvider.getLocales();
      const configuration = await this._RBMProvider.getConfiguration(locales);

      return Promise.resolve(configuration);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  getScreenConfiguration(
    screenId?: string | number,
    parentalRatings?: string
  ): Promise<IScreenModel | undefined> {
    return this._RBMProvider.getComponentConfiguration(
      screenId,
      parentalRatings
    );
  }

  getScreenComponentBody(screenName: string) {
    return this._RBMProvider.getScreenComponentBody(screenName);
  }

  getAssetList(params: IAssetQueryParameters): Promise<IMediaModel[]> {
    return this._RBMProvider.getAssetList(params);
  }

  getMedia(options: IMediaOptionsModel): Promise<IMediaModel> {
    return this._RBMProvider.getMedia(options);
  }

  getMediaExtraMaterials(extras: string[]): Promise<IMediaModel[]> {
    return this._RBMProvider.getMediaExtraMaterials(extras);
  }

  getMediaProgress(media: IMediaModel): Promise<IMediaModel> {
    return this._RBMProvider.getMediaProgress(media);
  }

  getNextMedia(
    mediaId: string | number,
    collectionId?: string,
    playlistId?: string
  ) {
    return this._RBMProvider.getNextMedia(mediaId, collectionId, playlistId);
  }

  getPrevMedia(mediaId: IMediaModel, collectionId?: string) {
    return this._RBMProvider.getPrevMedia(mediaId, collectionId);
  }

  searchMedia(
    filter: IMediaSearchFilterModel
  ): Promise<IMediaSearchStateModel> {
    return this._RBMProvider.searchMedia(filter);
  }

  searchPeople(
    filter: IPeopleSearchFilterModel
  ): Promise<IPeopleSearchStateModel> {
    return this._RBMProvider.searchPeople(filter);
  }

  searchMulti(
    filter: IMediaSearchFilterModel
  ): Promise<IMultiSearchStateModel> {
    return this._RBMProvider.searchMulti(filter);
  }

  getMediaList(options: IMediaListOptionsModel): Promise<IMediaListModel> {
    return this._RBMProvider.getMediaList(options);
  }

  getMediaCategories(tagType?: boolean): Promise<IMediaCategoryListModel> {
    return this._RBMProvider.getMediaCategories(tagType);
  }

  getUserDevices(): Promise<IUserDeviceModel[]> {
    return this._RBMProvider.getUserDevices();
  }

  getUserPlayDevices(): Promise<IUserDeviceModel[]> {
    return this._RBMProvider.getUserPlayDevices();
  }

  deleteUserDevice(id: string): Promise<void> {
    return this._RBMProvider.deleteUserDevice(id);
  }

  deleteUserPlayDevice(id: string): Promise<void> {
    return this._RBMProvider.deleteUserPlayDevice(id);
  }

  getUserFavorites(): Promise<IUserFavoritesModel[]> {
    return this._RBMProvider.getUserFavorites();
  }
  addToMyList(mediaId: string | number): Promise<IMediaModel> {
    return this._RBMProvider.addToMyList(mediaId);
  }
  removeFromMyList(mediaId: string | number): Promise<void> {
    return this._RBMProvider.removeFromMyList(mediaId);
  }
  getLastViewEpisode(mediaId: string | number): Promise<IMediaModel> {
    return this._RBMProvider.getLastViewEpisode(mediaId);
  }
  getUserSessionDetails(): Promise<ISessionModel> {
    return this._RBMProvider.getUserSessionDetails();
  }
  getUserAvailabilityKeys(): Promise<IUserAvailabilityKeysModel> {
    return this._RBMProvider.getUserAvailabilityKeys();
  }
  getUserPlayHistory(
    params: IUserPlayHistoryRequestModel
  ): Promise<IPlayHistoryListModel> {
    return this._RBMProvider.getUserPlayHistory(params);
  }
  getUserWatchHistory(): Promise<IMediaModel[]> {
    return this._RBMProvider.getUserWatchHistory();
  }
  getUserPreferences(assetIds: string[]): Promise<IUserPreferencesModel> {
    return this._RBMProvider.getUserPreferences(assetIds);
  }
  deleteUserWatchHistory(id: string | number): Promise<void> {
    return this._RBMProvider.deleteUserWatchHistory(id);
  }
  signIn(data: IAuthRequestModel): Observable<IAuthResponseModel> {
    return this._SkymillProvider.signIn(data);
  }

  signUp(data: IAuthSignUpRequestModel): Promise<IAuthSignUpResponseModel> {
    return this._SkymillProvider.signUp(data);
  }

  validatePassword(password: string, email?: string): Promise<boolean> {
    return this._SkymillProvider.validatePassword(password, email);
  }

  signInViaToken(
    data: IAuthViaTokenRequestModel
  ): Observable<IAuthResponseModel> {
    return this._SkymillProvider.signInViaToken(data);
  }

  loginViaToken(
    data: IAuthViaTokenRequestModel
  ): Observable<IAuthResponseModel> {
    return this._SkymillProvider.loginViaToken(data);
  }

  async signOut(device: IUserDeviceModel): Promise<void> {
    try {
      const session = StorageHelper.getSession();
      if (session?.RefreshToken) {
        await this._SkymillProvider.signOut(session?.RefreshToken);
      }
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }
  refreshToken(
    refreshToken: string,
    device: IUserDeviceModel
  ): Promise<IAuthResponseModel> {
    return Promise.reject("Not implemented");
  }
  changePassword(
    data: IChangePasswordModel
  ): Promise<IChangePasswordResponseModel> {
    return this._SkymillProvider.changePassword(data);
  }
  getProfile(): Promise<IUserModel> {
    return this._SkymillProvider.getProfile();
  }
  updateProfile(data: Partial<IUserModel>): Promise<IUserModel> {
    return this._SkymillProvider.updateProfile(data);
  }
  getUserConsents(locale?: string): Promise<IUserConsentModel[]> {
    return this._SkymillProvider.getUserConsents(locale);
  }
  updateUserConsents(
    data: IUserConsentModel[],
    locale?: string
  ): Observable<IUserConsentModel[]> {
    return this._SkymillProvider.updateUserConsents(data, locale);
  }
  getOffers(data?: IUserUtmSource): Observable<IPaymentOfferModel[]> {
    return this._SkymillProvider.getOffers({
      utm_source: data?.UtmSource,
      utm_medium: data?.UtmMedium,
      utm_campaign: data?.UtmCampaign,
    });
  }
  getProducts(): Promise<IProductModel[]> {
    return this._SkymillProvider.getProducts();
  }
  createPaymentOrder(data: ICreatePaymentOrderRequestModel) {
    return this._SkymillProvider.createPaymentOrder(data);
  }
  getPaymentInvoices() {
    return this._SkymillProvider.getPaymentInvoices();
  }
  getInvoicePDF(invoiceId: string) {
    return this._SkymillProvider.getInvoicePDF(invoiceId);
  }
  getPaymentDetails() {
    return this._SkymillProvider.getPaymentDetails();
  }
  createPaymentDetails(
    data: ICreatePaymentDetailsModel
  ): Promise<IPaymentOrderModel> {
    return this._SkymillProvider.createPaymentDetails(data);
  }
  validateCoupon(coupon: string): Promise<IValidCouponModel> {
    return this._SkymillProvider.validateCoupon(coupon);
  }
  getOrderStatus(orderId: string): Promise<IOrderStatusModel> {
    return this._SkymillProvider.getOrderStatus(orderId);
  }
  getUserSubscriptions(): Observable<IUserSubscriptionModel[] | IErrorModel> {
    return this._SkymillProvider.getUserSubscriptions();
  }
  cancelSubscription(
    data: ICancelSubscriptionRequestModel
  ): Promise<ICancelSubscriptionModel> {
    return this._SkymillProvider.cancelUserSubscription(data);
  }
  uncancelSubscription(id: number): Promise<void> {
    return this._SkymillProvider.uncancelUserSubscription(id);
  }
  pauseSubscription(
    data: IPauseSubscriptionRequestModel
  ): Promise<IPauseSubscriptionModel> {
    return this._SkymillProvider.pauseUserSubscription(data);
  }

  getUserSubprofiles(
    currentSubprofileId?: string,
    defaultMainSubprofileName?: string
  ): Observable<IUserSubprofileModel[]> {
    return this._RBMProvider.getUserSubprofiles(
      currentSubprofileId,
      defaultMainSubprofileName
    );
  }

  createUserSubprofile(
    data: IUserSubprofileModel,
    currentSubprofileId?: string,
    defaultMainSubprofileName?: string
  ): Promise<IUserSubprofileModel[]> {
    return this._RBMProvider.createUserSubprofile(
      data,
      currentSubprofileId,
      defaultMainSubprofileName
    );
  }

  updateUserSubprofile(
    data: IUserSubprofileModel,
    id: string | number
  ): Promise<IUserSubprofileModel> {
    return this._RBMProvider.updateUserSubprofile(data, id);
  }

  deleteUserSubprofile(id: string | number): Promise<any> {
    return this._RBMProvider.deleteUserSubprofile(id);
  }

  selectUserSubprofile(
    userId: string,
    remember: boolean
  ): Promise<ITokenModel | undefined> {
    return this._RBMProvider.selectUserSubprofile(userId, remember);
  }

  getUserParentalRatings(
    countryCode: string
  ): Observable<IUserParentalRatingModel[]> {
    return this._RBMProvider.getUserParentalRatings(countryCode);
  }

  getUserSubprofilePinCode(): Observable<string | undefined> {
    return this._RBMProvider.getUserSubprofilePinCode();
  }

  setUserSubprofilePinCode(
    pinCode: string,
    pinCodeId?: string
  ): Promise<string> {
    return this._RBMProvider.setUserSubprofilePinCode(pinCode, pinCodeId);
  }

  validateUserSubprofilePinCode(
    pinCodeId: string,
    pinCode: string
  ): Promise<boolean> {
    return this._RBMProvider.validateUserSubprofilePinCode(pinCodeId, pinCode);
  }

  deleteUserSubprofilePinCode(pinCodeId: string): Promise<void> {
    return this._RBMProvider.deleteUserSubprofilePinCode(pinCodeId);
  }

  getLoginCode(): Promise<ILoginCodeResponseModel> {
    return this._SkymillProvider.getLoginCode();
  }

  verifyLogin(data: ILoginCodeModel): Promise<IVerifyLoginCodeResponseModel> {
    return this._SkymillProvider.verifyLoginCode(data);
  }

  getLoginTokenFromCode(
    data: ILoginCodeModel
  ): Promise<IGetLoginTokenFromCodeModel> {
    return this._SkymillProvider.getLoginTokenFromCode(data);
  }

  getOfferSwitches(): Promise<IOfferSwitchModel[]> {
    return this._SkymillProvider.getOfferSwitches();
  }

  switchSubscription(data: ISubscriptionSwitchRequestModel): Promise<void> {
    return this._SkymillProvider.switchUserSubscription(data);
  }

  cancelSubscriptionSwitch(): Promise<void> {
    return this._SkymillProvider.cancelUserSubscriptionSwitch();
  }

  getUserFavouriteListByComponentUrl(url: string): Observable<IMediaModel[]> {
    return this._RBMProvider.getUserFavouriteListByComponentUrl(url);
  }

  getCustomer(): Observable<IUserInfoModel> {
    return this._SkymillProvider.getCustomer();
  }

  getProductOffers(): Observable<any> {
    return this._RBMProvider.getProductOffers();
  }

  getProductOfferingIds(): Observable<string[]> {
    return this._RBMProvider.getProductOfferingIds();
  }

  getAccountPurchase(): Observable<any> {
    return this._RBMProvider.getAccountPurchase();
  }

  getChannelOnNow(channelId: string) {
    return this._RBMProvider.getChannelOnNow(channelId);
  }

  getUserCountry() {
    return this._SkymillProvider.getUserCountry();
  }
}
